import { useState, useEffect, useRef } from "@wordpress/element";
import AsyncSelect from "react-select/async";
import { reactSelectStylesAjax } from "../../others/helperFunctions";
import Description from "./common/Description";

const CustomAsyncSelect = ({
    label,
    value,
    onChange,
    name,
    placeholder = "Search and select...",
    description,
    className = "",
    containerClass = "",
    endpoint,
    apiURL,
    allOptions = null,
    formatOptionLabel,
    transformData,
    searchParams = {},
    selectedLabel = "selected",
    selectedLabelPlural,
    noOptionsText = "Start typing to search...",
    isMulti = true,
    width,
    ...props
}) => {
    const [selectedItems, setSelectedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const prevValueRef = useRef();

    // Load initial selected items when component mounts or value changes
    useEffect(() => {
        if (isMulti) {
            const currentValue = JSON.stringify(value);
            const prevValue = JSON.stringify(prevValueRef.current);

            if (currentValue !== prevValue) {
                prevValueRef.current = value;

                if (value && value.length > 0) {
                    loadSelectedItems(value);
                } else {
                    setSelectedItems([]);
                }
            }
        } else {
            if (value !== prevValueRef.current) {
                prevValueRef.current = value;

                if (value) {
                    loadSelectedItems([value]);
                } else {
                    setSelectedItems([]);
                }
            }
        }
    }, [isMulti, value]);

    // Load selected items by IDs (for initial render)
    const loadSelectedItems = async (itemIds) => {
        if (!itemIds || itemIds.length === 0) return;

        setIsLoading(true);
        try {
            const response = await fetch(
                `${WPBEAN_QC_API.rest_root}${endpoint}?include=${itemIds.join(
                    ",",
                )}&per_page=100`,
                {
                    headers: {
                        "X-WP-Nonce": WPBEAN_QC_API.nonce,
                    },
                },
            );

            if (!response.ok) {
                throw new Error("Failed to load items");
            }

            const items = await response.json();

            // ✅ FIX: Preserve selection order instead of using API order
            // Create a map of ID to transformed data for quick lookup
            const itemsMap = {};
            items.forEach((item) => {
                const transformed = transformData(item);
                itemsMap[transformed.value] = transformed;
            });

            // Map itemIds in the order they were selected
            const orderedOptions = itemIds
                .map((id) => itemsMap[id])
                .filter(Boolean); // Remove any undefined items

            setSelectedItems(orderedOptions);
        } catch (error) {
            console.error("Error loading selected items:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Load items with search (AJAX)
    const loadItems = async (inputValue) => {
        if (allOptions) {
            const filteredOptions = allOptions.filter((option) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase()),
            );
            return filteredOptions;
        }

        try {
            const searchParam = inputValue
                ? `&search=${encodeURIComponent(inputValue)}`
                : "";

            // Build additional search params
            const additionalParams = Object.entries(searchParams)
                .map(([key, val]) => `&${key}=${val}`)
                .join("");

            const response = await fetch(
                `${WPBEAN_QC_API.rest_root}${endpoint}?per_page=50${searchParam}${additionalParams}`,
                {
                    headers: {
                        "X-WP-Nonce": WPBEAN_QC_API.nonce,
                    },
                },
            );

            if (!response.ok) {
                throw new Error("Failed to load items");
            }

            const items = await response.json();
            return items.map(transformData); // Ensure `transformData` is applied if allOptions is not used.
        } catch (error) {
            console.error("Error loading items:", error);
            return [];
        }
    };

    // Handle selection change
    const handleChange = (selected) => {
        setSelectedItems(isMulti ? selected || [] : selected ? [selected] : []);
        if (isMulti) {
            const itemIds = selected ? selected.map((item) => item.value) : [];
            onChange(name, itemIds);
        } else {
            onChange(name, selected ? selected.value : "");
        }
    };

    const loadingSpinner = (
        <div className="flex items-center justify-center gap-2">
            <svg
                className="animate-spin h-3 w-3 text-blue-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
            >
                <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="3"
                ></circle>
                <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
            </svg>
        </div>
    );

    return (
        <div className={containerClass}>
            {label && (
                <div className="flex items-center gap-2 mb-2">
                    <h3 className="block text-sm font-medium text-gray-700 ">
                        {label}
                    </h3>
                    {isLoading && loadingSpinner}
                </div>
            )}

            {/* Wrapper with relative positioning for overlay spinner */}
            <div className="relative">
                <AsyncSelect
                    isMulti={isMulti}
                    cacheOptions
                    defaultOptions
                    value={isMulti ? selectedItems : selectedItems[0] || null}
                    onChange={handleChange}
                    loadOptions={loadItems}
                    isLoading={false} // Disable default spinner
                    placeholder={placeholder}
                    styles={reactSelectStylesAjax({ width, isMulti })}
                    noOptionsMessage={({ inputValue }) =>
                        inputValue
                            ? `No results found for "${inputValue}"`
                            : noOptionsText
                    }
                    loadingMessage={() => "Loading..."}
                    formatOptionLabel={formatOptionLabel}
                    isClearable={!isMulti}
                    closeMenuOnSelect={!isMulti}
                    blurInputOnSelect={!isMulti}
                    className={`react-select-container max-wp:!w-auto ${className}`}
                    classNamePrefix="react-select"
                    {...props}
                />
            </div>

            <Description description={description} />

            {/* Display count of selected items */}
            {selectedItems.length > 0 && selectedLabel && (
                <p className="mt-2 text-xs text-blue-600 font-medium">
                    {selectedItems.length}{" "}
                    {selectedItems.length === 1
                        ? selectedLabel
                        : selectedLabelPlural
                        ? selectedLabelPlural
                        : `${selectedLabel}s`}
                </p>
            )}
        </div>
    );
};

export default CustomAsyncSelect;
